import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { SERVICE_TYPES, serviceMeta, Badge } from "@/lib/ui";
import { MapPin, Calendar, AlertTriangle, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import LocationFilter, { DistanceTag } from "@/components/LocationFilter";

const selectCls = "px-4 min-h-[48px] rounded-xl border border-[#E2D9CE] bg-white focus:border-[#D96B43] outline-none font-medium";

export default function Browse() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ service_type: "all", payment_type: "all", urgency: "all" });
  const [geo, setGeo] = useState({});
  const [area, setArea] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = { ...filters, ...geo, area: area || undefined };
    const t = setTimeout(() => {
      api.get("/requests", { params }).then((r) => setRequests(r.data)).finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [filters, geo, area]);

  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">Requests for help</h1>
          <p className="text-lg text-slate-600 mt-1">Browse open requests from neighbours who need a hand.</p>
        </div>
        {(!user || user.role === "seeker" || user.role === "admin") && (
          <Link to="/post" data-testid="post-request-btn"
            className="bg-[#D96B43] hover:bg-[#C25832] text-white font-semibold px-6 min-h-[48px] rounded-xl flex items-center gap-2 transition-colors">
            <Plus className="w-5 h-5" /> Post a request
          </Link>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-8">
        <select data-testid="filter-service-type" value={filters.service_type} onChange={set("service_type")} className={selectCls}>
          <option value="all">All services</option>
          {SERVICE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select data-testid="filter-payment-type" value={filters.payment_type} onChange={set("payment_type")} className={selectCls}>
          <option value="all">Paid & Voluntary</option>
          <option value="voluntary">Voluntary only</option>
          <option value="paid">Paid only</option>
        </select>
        <select data-testid="filter-urgency" value={filters.urgency} onChange={set("urgency")} className={selectCls}>
          <option value="all">Any urgency</option>
          <option value="urgent">Urgent</option>
          <option value="normal">Normal</option>
        </select>
        <LocationFilter geo={geo} setGeo={setGeo} area={area} setArea={setArea} savedCoords={user} />
      </div>

      {loading ? (
        <p className="text-lg text-slate-500">Loading requests…</p>
      ) : requests.length === 0 ? (
        <div className="bg-white border border-[#E2D9CE] rounded-2xl p-10 text-center">
          <p className="text-xl font-semibold text-slate-800">No open requests match your filters.</p>
          <p className="text-slate-600 mt-1">Try adjusting the filters above.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {requests.map((r) => {
            const meta = serviceMeta(r.service_type);
            return (
              <div key={r.id} data-testid={`request-card-${r.id}`}
                className="bg-white border border-[#E2D9CE] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="w-11 h-11 rounded-xl bg-[#FBEBE4] flex items-center justify-center">
                      <meta.icon className="w-6 h-6 text-[#D96B43]" />
                    </span>
                    <div className="flex gap-2">
                      {r.urgency === "urgent" && <Badge color="red"><AlertTriangle className="w-4 h-4" /> Urgent</Badge>}
                      <Badge color={r.payment_type === "paid" ? "orange" : "teal"}>
                        {r.payment_type === "paid" ? `$${r.amount} Paid` : "Voluntary"}
                      </Badge>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900">{r.title}</h3>
                  <p className="text-slate-600 mt-1 line-clamp-2">{r.description}</p>
                  <div className="mt-4 space-y-1 text-slate-600">
                    <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#2E7D6B]" /> {r.location}</p>
                    <DistanceTag km={r.distance_km} />
                    <p className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#2E7D6B]" /> {r.scheduled_date}</p>
                  </div>
                </div>
                <Link to={`/requests/${r.id}`} data-testid={`view-request-${r.id}`}
                  className="mt-5 border-2 border-[#E2D9CE] hover:border-[#D96B43] text-slate-800 font-semibold min-h-[48px] rounded-xl flex items-center justify-center transition-colors">
                  View details
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
